import { z } from "zod";
import { propertyIdSchema } from "./property-schemas";

const MEBIBYTE = 1024 * 1024;
const MAX_IMAGE_BYTES = 10 * MEBIBYTE;
const MAX_FILENAME_BYTES = 255;

const utf8ByteLength = (value: string): number => new TextEncoder().encode(value).byteLength;
const mediaIdSchema = propertyIdSchema;
const versionNoSchema = z.number().int().min(1).max(2_147_483_647);

export const privateImagePurposeSchema = z.enum(["PROFILE_IMAGE", "PROPERTY_IMAGE"]);
export const privateImageMimeTypeSchema = z.enum(["image/jpeg", "image/png", "image/webp"]);

export const privateImageMediaSchema = z.strictObject({
  mediaId: mediaIdSchema,
  purpose: privateImagePurposeSchema,
  mimeType: privateImageMimeTypeSchema,
  byteSize: z.number().int().min(1).max(MAX_IMAGE_BYTES),
});

export const registerPrivateImageUploadInputSchema = z.strictObject({
  purpose: privateImagePurposeSchema,
  originalFilename: z
    .string()
    .trim()
    .min(1)
    .max(MAX_FILENAME_BYTES)
    .refine(
      (value) => utf8ByteLength(value) <= MAX_FILENAME_BYTES,
      `Use no more than ${MAX_FILENAME_BYTES} bytes`,
    )
    .refine(
      (value) =>
        !value.includes("/") &&
        !value.includes("\\") &&
        !Array.from(value).some((character) => {
          const codePoint = character.codePointAt(0);
          return codePoint !== undefined && (codePoint <= 0x1f || codePoint === 0x7f);
        }),
      "Use a filename without path or control characters",
    ),
  mimeType: privateImageMimeTypeSchema,
  byteSize: z.number().int().min(1).max(MAX_IMAGE_BYTES),
});

export const privateImageUploadSchema = z.strictObject({
  mediaId: mediaIdSchema,
  uploadUrl: z.string().url().max(8_192),
  expiresAt: z.string().datetime({ offset: true }),
});

export const registerPrivateImageUploadResponseSchema = z.strictObject({
  upload: privateImageUploadSchema,
});
export const completePrivateImageUploadResponseSchema = z.strictObject({
  media: privateImageMediaSchema,
});
export const getProfileImageResponseSchema = z
  .strictObject({
    media: privateImageMediaSchema.nullable(),
  })
  .superRefine(({ media }, context) => {
    if (media !== null && media.purpose !== "PROFILE_IMAGE") {
      context.addIssue({
        code: "custom",
        path: ["media", "purpose"],
        message: "Profile image metadata must have PROFILE_IMAGE purpose",
      });
    }
  });
export const putProfileImageInputSchema = z.strictObject({ mediaId: mediaIdSchema });
export const putProfileImageResponseSchema = z
  .strictObject({ media: privateImageMediaSchema })
  .superRefine(({ media }, context) => {
    if (media.purpose !== "PROFILE_IMAGE") {
      context.addIssue({
        code: "custom",
        path: ["media", "purpose"],
        message: "Profile image metadata must have PROFILE_IMAGE purpose",
      });
    }
  });

export const propertyImageSetSchema = z
  .strictObject({
    versionNo: versionNoSchema,
    media: z.array(privateImageMediaSchema).max(4),
  })
  .superRefine(({ media }, context) => {
    const mediaIds = new Set<string>();
    media.forEach((item, index) => {
      if (item.purpose !== "PROPERTY_IMAGE") {
        context.addIssue({
          code: "custom",
          path: ["media", index, "purpose"],
          message: "Property image metadata must have PROPERTY_IMAGE purpose",
        });
      }
      if (mediaIds.has(item.mediaId)) {
        context.addIssue({
          code: "custom",
          path: ["media", index, "mediaId"],
          message: "Property image identifiers must be unique",
        });
      }
      mediaIds.add(item.mediaId);
    });
  });

export const putPropertyImageSetInputSchema = z
  .strictObject({
    versionNo: versionNoSchema,
    mediaIds: z.array(mediaIdSchema).max(4),
  })
  .superRefine(({ mediaIds }, context) => {
    const seen = new Set<string>();
    mediaIds.forEach((mediaId, index) => {
      if (seen.has(mediaId)) {
        context.addIssue({
          code: "custom",
          path: ["mediaIds", index],
          message: "Property image identifiers must be unique",
        });
      }
      seen.add(mediaId);
    });
  });

export type PrivateImagePurpose = z.infer<typeof privateImagePurposeSchema>;
export type PrivateImageMimeType = z.infer<typeof privateImageMimeTypeSchema>;
export type PrivateImageMedia = z.infer<typeof privateImageMediaSchema>;
export type RegisterPrivateImageUploadInput = z.infer<typeof registerPrivateImageUploadInputSchema>;
export type PrivateImageUpload = z.infer<typeof privateImageUploadSchema>;
export type PutProfileImageInput = z.infer<typeof putProfileImageInputSchema>;
export type PropertyImageSet = z.infer<typeof propertyImageSetSchema>;
export type PutPropertyImageSetInput = z.infer<typeof putPropertyImageSetInputSchema>;
